<template>
  <div class="view-container">
    <div class="services-container">
      <div class="sub-header">
        <h2 class="page-title">لیست خدمات و کالاها</h2>
        <button class="add" @click="openModal">جدید</button>
      </div>
  <hr class="divider-line" />
      <div class="search">
        <input id="general-search" type="text" v-model="searchQuery" placeholder="جستجو..." />
        <button class="search-button" @click="fetchMatabService()">جستجو</button>
      </div>
      <Teleport to="body">
        <div v-if="floatingTooltip.visible" class="floating-tooltip" :style="{ left: floatingTooltip.left + 'px', top: floatingTooltip.top + 'px' }">{{ floatingTooltip.text }}</div>
      </Teleport>

      <BaseTable
        :items="filteredServicesList"
        keyField="fScode"
        :emptyColspan="5"
        :emptyText='"هیچ خدمتی/کالایی ثبت نشده است. برای شروع، روی دکمه \"جدید\" کلیک کنید."'
      >
        <template #head>
          <tr>
            <th>ردیف</th>
            <th>کد کالا/خدمت</th>
            <th>نوع کالا/خدمت</th>
            <th>نام کالا/خدمت</th>
            <th>عملیات</th>
          </tr>
        </template>

        <template #row="{ item: service, index }">
          <td>{{ index + 1 }}</td>
          <td>{{ service.fScode }}</td>
          <td>{{ service.shstype }}</td>
          <td>{{ service.fSname }}</td>
          <td class="actions-cell">
            <button
              class="action-btn edit"
              @click.stop="handleEdit(service)"
              aria-label="ویرایش"
              @mouseenter.stop="showFloatingTooltip($event, 'ویرایش')"
              @mouseleave.stop="hideFloatingTooltip()"
              @focus.stop="showFloatingTooltip($event, 'ویرایش')"
              @blur.stop="hideFloatingTooltip()"
            >
              <img src="/img/edit.svg" alt="ویرایش" />
            </button>
            <button
              class="action-btn delete"
              @click.stop="deleteService(service.fSauto)"
              aria-label="حذف"
              @mouseenter.stop="showFloatingTooltip($event, 'حذف')"
              @mouseleave.stop="hideFloatingTooltip()"
              @focus.stop="showFloatingTooltip($event, 'حذف')"
              @blur.stop="hideFloatingTooltip()"
            >
              <img src="/img/trash.svg" alt="حذف" />
            </button>
          </td>
        </template>
      </BaseTable>
    </div>

  <div v-if="isModalOpen" class="modal-overlay">
      <div class="modal-content">
        <div class="sub-header">
          <ModalHeader>
            <template #default>
              <h2 class="page-title">{{ formMode === 'add' ? 'افزودن اطلاعات خدمات و کالای جدید' : 'ویرایش اطلاعات خدمات و کالای جدید' }}</h2>
            </template>
          </ModalHeader>
          <div class="form-actions">
            <BaseButton title="ثبت" variant="primary" type="submit" form="service-form" />
            <BaseButton title="انصراف" variant="cancel" @click="closeModal" />
          </div>
        </div>
  <hr class="divider-line" />
        <form @submit.prevent="handleSubmit" class="service-form-container" id="service-form">
          <div class="top-section">
            <div class="top-form-fields">
              <div class="form-group">
                <label for="service-code">کد کالا/خدمت <span class="required">*</span></label>
                <input id="service-code" type="text" v-model="serviceForm.fScode" required />
              </div>
              <div class="form-group">
                <label for="service-type">نوع کالا/خدمت <span class="required">*</span></label>
                <select id="service-type" v-model="serviceForm.fStype">
                  <option value="">انتخاب کنید</option>
                  <option value="1">خدمت</option>
                  <option value="2">کالا</option>
                </select>
              </div>
              <div class="form-group">
                <label for="service-name">نام کالا/خدمت<span class="required">*</span></label>
                <input id="service-name" type="text" v-model="serviceForm.fSname" required />
              </div>
            </div>

            <div class="bottom-form-fields">
              <div class="form-group span-4">
                <label for="insurance-type">نوع بیمه</label>
                <select id="insurance-type" v-model="serviceForm.insuranceType">
                  <option value="">انتخاب کنید</option>
                  <option>تامین اجتماعی</option>
                  <option>خدمات درمانی</option>
                  <option>نیروهای مسلح</option>
                  <option>آزاد</option>
                  <option>تکمیلی</option>
                </select>
              </div>
              <div class="form-group">
                <label for="patient-share">سهم بیمار</label>
                <input id="patient-share" type="number" v-model="serviceForm.patientShare" />
              </div>
              <div class="form-group">
                <label for="insurance-share">سهم بیمه</label>
                <input id="insurance-share" type="number" v-model="serviceForm.insuranceShare" />
              </div>
            </div>

            <div class="form-group span-4">
              <label for="service-costs">سایر هزینه‌ها</label>
              <input id="service-costs" type="text" v-model="serviceForm.otherCosts" />
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useFloatingTooltip } from '@/composables/useFloatingTooltip'
import { storeToRefs } from "pinia";
import { useMatabServiceStore } from "@/stores/clinic/services.js"; // Import the store
import ModalHeader from '@/components/ModalHeader.vue'; // Import ModalHeader
import BaseButton from '@/components/UiCompunents/BaseButton.vue'
import BaseTable from '@/components/UiCompunents/BaseTable.vue'

// --- State ---
const isModalOpen = ref(false);
const formMode = ref("add");
const editingServiceId = ref(null);
// --- Store Setup ---
const servicesStore = useMatabServiceStore();
// Get the reactive list and the action from the store
const { matabServiceList, pagination, searchQuery, loading } = storeToRefs(servicesStore);
const { fetchMatabService, addMatabService, updateMatabService, deleteMatabService } = servicesStore;

const initialFormState = {
  fSauto: 0,
  fScode: "",
  fSname: "",
  fStype: 1,
  insuranceType: "",
  insuranceShare: "",
  patientShare: "",
};

let serviceForm = reactive({ ...initialFormState });

const filteredServicesList = computed(() => matabServiceList.value);
const query = searchQuery.value.toLowerCase().trim();

// --- Methods ---
function openModal() {
  formMode.value = "add";
  isModalOpen.value = true;
}
function closeModal() {
  isModalOpen.value = false;
  resetForm();
}

function resetForm() {
  Object.assign(serviceForm, initialFormState);
}

function handleSubmit() {
  // Call the store's action to add the service to the central list
  if (formMode.value === "add") {
    addMatabService({ ...serviceForm });
  } else {
    updateMatabService({ ...serviceForm });
  }

  resetForm();
  closeModal();
}

// NEW: Function to handle when the 'Edit' button is clicked
function handleEdit(service) {
  formMode.value = "edit";
  editingServiceId.value = service.id;
  Object.assign(serviceForm, service);
  isModalOpen.value = true;
}
async function deleteService(serviceId) {
  try {
    await deleteMatabService(serviceId);
    await fetchMatabService(pagination.value.current_page);
  } catch (err) {
    console.error(err);
    alert("خطا در حذف پزشک");
  }
}
onMounted(() => {
  fetchMatabService();
});

const { floatingTooltip, showFloatingTooltip, hideFloatingTooltip } = useFloatingTooltip()
</script>
<style scoped>
.view-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.header {
  width: 100%;
  text-align: right;
  color: var(--text-color-primary);
  padding: 10px;

  border-radius: 20px;
}

.sub-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: var(--text-color-secondary);

  .add {
    display: flex;
    align-items: center;
    padding: 10px 22px;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    background-color: var(--background-color-button);
    color: var(--background-color);

    &:hover {
      background-color: var(--background-color-button-hover);
    }
  }
}

.services-container {
  direction: rtl;
  background-color: var(--background-color);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* The main form container is now a flexbox column */
.service-form-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  /* Gap between top and bottom sections */
}


.top-form-fields {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

/* --- End of new styles --- */

.top-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bottom-form-fields {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 5px;
  color: var(--tex-color-thirdly);
  text-align: justify;
}

.required {
  color: #ff0f0f;
  margin-right: 4px;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 5px;
  border: 1px solid var(--text-color-secondary);
  border-radius: 10px;
  text-align: start;
  width: 100%;
  background-color: var(--background-color-secondary);
  color: var(--text-color-primary);
}

.form-group input,
.form-group select {
  height: 34px;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--text-color-primary);
}

.form-actions {
  display: flex;
  justify-content: flex-start;
}

  /* submit-button replaced by BaseButton (variant="primary") */



input#general-search {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid var(--text-color-secondary);
  border-radius: 10px;
  text-align: start;
  background-color: var(--background-color);
  color: var(--text-color-primary);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.actions-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px; /* match clinic-doctors spacing */
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  margin: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: calc(0.2vw + 14px);
  height: 100%;
  min-height: 32px;
  width: 100%;
  /* keep same look for tooltips */
}

.action-btn img {
  width: 18px;
  height: 18px;
}

.action-btn .svg-inline--fa,
.action-btn .fa-icon,
.action-btn svg {
  font-size: 14px;
  width: 18px;
  height: 18px;
}

/* Inline tooltip CSS removed — floating tooltip used instead */

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: calc(100dvh - 65px);
  margin-top: 65px;
  background-color: rgba(var(--text-color-secondary-rgb), 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: var(--background-color);
  padding: 20px;
  border-radius: 30px;
  width: auto;
  max-height: 90vh;
  max-width: 90vw;
  overflow-y: auto;
  position: relative;
  direction: rtl;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 10px;
}

.modal-close-btn {
  border: none;
  font-size: calc(0.3vw + 12px);
  color: var(--text-color-secondary);
  background-color: transparent;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: pointer;
}

input#general-search {
  width: auto;
  flex: 1;
  padding: 6px 8px;
  border: 1px solid var(--text-color-secondary);
  border-radius: 10px;
  text-align: start;
  background-color: var(--background-color);
  color: var(--text-color-primary);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

input#general-search:focus {
  outline: none;
  border-color: var(--text-color-primary);
  box-shadow: 0 0 0 2px rgba(var(--background-color-button-rgb), 0.12);
}

input#general-search::placeholder {
  color: var(--text-color-secondary);
  opacity: 1;
}

.search {
  display: flex;
  gap: 8px;
  align-items: center;
}

.search-button {
  padding: 8px 14px;
  border-radius: 8px;
  border: none;
  background-color: var(--background-color-button);
  color: var(--background-color);
  cursor: pointer;
}

.search-button:hover {
  background-color: var(--background-color-button-hover);
}

@media (max-width: 768px) {

  .top-form-fields,
  .bottom-form-fields {
    grid-template-columns: 1fr;
  }

  .form-group.span-4 {
    grid-column: span 1;
  }

  .image-upload-area {
    min-height: 200px;
  }
}
</style>
